import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import {
  Autocomplete,
  alpha,
  Box,
  Button,
  Paper,
  type PaperProps,
  TextField,
  Typography,
} from '@mui/material';
import type { MembershipSearchItem } from '@shared/dto/auth/membership-search.response.dto';
import { createContext, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMembershipSearch } from '../../hooks/auth/use-membership-search';
import { useDebouncedValue } from '../../hooks/use-debounced-value';
import { colors } from '../../theme/ai-theme';
import { CompanyOptionContent } from './company-option-content';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

export interface CompanyPickerAutocompleteProps {
  currentTenantId?: string | null;
  currentTenantName?: string | null;
  onSelect: (tenantId: string, tenantName: string) => void;
  /** Powers the "view all N companies" footer of the popup; omit it to hide the footer. */
  onOpenAdvancedSearch?: () => void;
  variant: 'navbar' | 'page';
  autoFocus?: boolean;
  /** Opens the company list as soon as the field is focused (recognition over recall). */
  openOnFocus?: boolean;
  /** Test seam: the default 300ms is what users get. */
  debounceMs?: number;
}

const navbarSx = {
  // Accanto al drawer da 260px, tra 900 e 1200px resta poco spazio per il titolo.
  width: { xs: 240, md: 260, lg: 320 },
};

// Sul TextField e non sull'Autocomplete: il tema (MuiTextField.styleOverrides) dà
// sfondo bianco e testo scuro a ogni input con la stessa specificità, e solo l'sx
// del TextField stesso è garantito vincere in ogni build.
const navbarTextFieldSx = {
  // Specificità più alta del padding verticale che Autocomplete mette sull'input root.
  '&& .MuiOutlinedInput-root.MuiInputBase-root': { height: 36, paddingTop: 0, paddingBottom: 0 },
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    color: colors.navigation.text,
    backgroundColor: alpha(colors.navigation.text, 0.08),
    transition: 'background-color 150ms ease',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    '& .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover': { backgroundColor: alpha(colors.navigation.text, 0.12) },
    '&.Mui-focused': {
      backgroundColor: alpha(colors.navigation.text, 0.14),
      boxShadow: 'none',
      outline: `2px solid ${colors.navigation.text}`,
      outlineOffset: 2,
    },
  },
  '& .MuiInputBase-input': {
    color: colors.navigation.text,
    fontWeight: 600,
    fontSize: 16,
    '&::placeholder': { color: colors.navigation.text, opacity: 1 },
    '&:focus::placeholder': { opacity: 0.6 },
  },
  '& .MuiSvgIcon-root, & .MuiIconButton-root': { color: colors.navigation.text },
};

interface ViewAllFooter {
  total: number;
  onViewAll: () => void;
}

const ViewAllFooterContext = createContext<ViewAllFooter | null>(null);

function CompanyPickerPaper({ children, ...paperProps }: PaperProps) {
  const { t } = useTranslation();
  const footer = useContext(ViewAllFooterContext);
  return (
    <Paper {...paperProps}>
      {children}
      {footer && (
        <Box sx={{ borderTop: 1, borderColor: 'divider', p: 0.5 }}>
          <Button fullWidth sx={{ minHeight: 48 }} onClick={footer.onViewAll}>
            {t('consultant.companyPicker.viewAll', { count: footer.total })}
          </Button>
        </Box>
      )}
    </Paper>
  );
}

const pageSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': { minHeight: 48 },
};

export function CompanyPickerAutocomplete({
  currentTenantId,
  currentTenantName,
  onSelect,
  onOpenAdvancedSearch,
  variant,
  autoFocus,
  openOnFocus,
  debounceMs = SEARCH_DEBOUNCE_MS,
}: CompanyPickerAutocompleteProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const debouncedSearch = useDebouncedValue(inputValue.trim(), debounceMs);
  const {
    data: membershipSearch,
    isFetching,
    isError,
    refetch,
  } = useMembershipSearch(
    { search: debouncedSearch || undefined, page: 1, pageSize: PAGE_SIZE },
    { enabled: isOpen },
  );
  const companyOptions: MembershipSearchItem[] = isError ? [] : (membershipSearch?.items ?? []);
  const isNavbar = variant === 'navbar';
  const switchLabel = t('consultant.switchCompany');
  const total = membershipSearch?.total ?? 0;
  const viewAllFooter: ViewAllFooter | null =
    onOpenAdvancedSearch && !isError && total > companyOptions.length
      ? {
          total,
          onViewAll: () => {
            setIsOpen(false);
            onOpenAdvancedSearch();
          },
        }
      : null;

  function handleSelect(membership: MembershipSearchItem | null) {
    setInputValue('');
    if (!membership || membership.tenantId === currentTenantId) {
      return;
    }
    onSelect(membership.tenantId, membership.tenantName);
  }

  return (
    <ViewAllFooterContext.Provider value={viewAllFooter}>
      <Autocomplete<MembershipSearchItem, false, false, false>
        value={null}
        openOnFocus={openOnFocus}
        forcePopupIcon
        options={companyOptions}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        inputValue={inputValue}
        onInputChange={(_event, nextValue, reason) => {
          if (reason !== 'reset') {
            setInputValue(nextValue);
          }
        }}
        onChange={(_event, membership) => handleSelect(membership)}
        filterOptions={(allOptions) => allOptions}
        getOptionLabel={(membership) => membership.tenantName}
        isOptionEqualToValue={(option, candidate) => option.tenantId === candidate.tenantId}
        loading={isFetching && !isError}
        loadingText={t('common.loading')}
        noOptionsText={
          isError ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: 'error.main' }}>
                {t('consultant.companyPicker.loadError')}
              </Typography>
              <Button size="small" sx={{ minHeight: 48 }} onClick={() => refetch()}>
                {t('consultant.companyPicker.retry')}
              </Button>
            </Box>
          ) : (
            t('consultant.noSearchResults')
          )
        }
        blurOnSelect
        handleHomeEndKeys
        sx={isNavbar ? navbarSx : pageSx}
        slots={{ paper: CompanyPickerPaper }}
        slotProps={{
          popper: { sx: { minWidth: isNavbar ? 360 : undefined } },
          // Bianco su sfondo pagina chiaro: senza bordo e ombra marcata il menu si confonde.
          paper: {
            sx: {
              mt: 0.75,
              border: 1,
              borderColor: 'divider',
              boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18), 0 2px 8px rgba(15, 23, 42, 0.08)',
            },
          },
          listbox: { sx: { maxHeight: 360 } },
        }}
        renderOption={(optionProps, membership) => {
          const { key, ...rest } = optionProps;
          const isCurrent = membership.tenantId === currentTenantId;
          return (
            <Box
              component="li"
              key={key}
              {...rest}
              aria-current={isCurrent ? 'true' : undefined}
              sx={{ minHeight: 48, py: 1 }}
            >
              <CompanyOptionContent membership={membership} isCurrent={isCurrent} />
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus={autoFocus}
            sx={isNavbar ? navbarTextFieldSx : undefined}
            label={isNavbar ? undefined : t('consultant.companyPicker.searchLabel')}
            placeholder={
              isNavbar
                ? (currentTenantName ?? switchLabel)
                : t('consultant.companyPicker.searchPlaceholder')
            }
            slotProps={{
              ...params.slotProps,
              htmlInput: {
                ...params.slotProps.htmlInput,
                'aria-label': isNavbar
                  ? t('consultant.switchCompanyCurrent', { name: currentTenantName ?? '' })
                  : undefined,
                style: { fontSize: 16 },
              },
              input: {
                ...params.slotProps.input,
                startAdornment: isNavbar ? (
                  <BusinessOutlinedIcon aria-hidden sx={{ fontSize: 20, ml: 0.5 }} />
                ) : (
                  params.slotProps.input.startAdornment
                ),
              },
            }}
          />
        )}
      />
    </ViewAllFooterContext.Provider>
  );
}
